import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Loader2, Phone } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn } from "./AdminUI";

export default function AdminSupport() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try { const { data } = await endpoints.adminSupportConversations(); setConvos(data.conversations || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);

  const loadThread = useCallback(async (userId) => {
    try { const { data } = await endpoints.adminSupportThread(userId); setMessages(data.messages || []); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 10000); return () => clearInterval(t); }, [loadConvos]);
  useEffect(() => {
    if (!active) return;
    loadThread(active.userId);
    const t = setInterval(() => loadThread(active.userId), 5000);
    return () => clearInterval(t);
  }, [active, loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openConvo = (c) => { setActive(c); setConvos((prev) => prev.map((x) => x.userId === c.userId ? { ...x, unread: 0 } : x)); };

  const reply = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setSending(true);
    try { const { data } = await endpoints.adminSupportReply(active.userId, t); setMessages((m) => [...m, data]); setText(""); loadConvos(); }
    catch { /* noop */ } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="Support Inbox" desc="Customer messages. Reply here — the customer also gets a WhatsApp nudge." />
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-0 overflow-hidden md:col-span-1 max-h-[70vh] overflow-y-auto" data-testid="support-conversations">
          {loading ? <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E11D2A]" /></div>
            : convos.length === 0 ? <div className="p-6 text-slate-500 text-sm text-center">No customer messages yet.</div>
            : convos.map((c) => (
              <button key={c.userId} onClick={() => openConvo(c)} data-testid={`convo-${c.userId}`}
                className={`w-full text-left px-4 py-3 border-b border-[#273042] hover:bg-white/5 ${active?.userId === c.userId ? "bg-white/5" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold truncate">{c.userName || "Customer"}</span>
                  {c.unread > 0 && <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-[#E11D2A] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`convo-unread-${c.userId}`}>{c.unread}</span>}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {c.userPhone || "—"}</div>
                <div className="text-xs text-slate-400 truncate mt-0.5">{c.lastSender === "staff" ? "You: " : ""}{c.lastText}</div>
              </button>
            ))}
        </Card>

        <Card className="md:col-span-2 p-0 flex flex-col max-h-[70vh]" data-testid="support-thread">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-500 min-h-[300px]">
              <MessageSquare className="w-12 h-12 mb-3" /> Select a conversation to reply
            </div>
          ) : (
            <>
              <div className="px-4 h-14 flex items-center border-b border-[#273042] text-white font-bold">{active.userName} <span className="ml-2 text-xs text-slate-400 font-normal">{active.userPhone}</span></div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[280px]">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "staff" ? "bg-[#2563EB] text-white" : "bg-[#1A1F2B] text-slate-100 border border-[#273042]"}`}>
                      {m.sender === "staff" && <div className="text-[10px] text-[#BBD0FF] font-semibold mb-0.5">{m.staffName || "Support"}</div>}
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className="text-[9px] opacity-60 mt-1">{m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[#273042] flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()}
                  placeholder="Type your reply…" data-testid="support-reply-input"
                  className="flex-1 min-w-0 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#2563EB]/60" />
                <Btn onClick={reply} disabled={sending} data-testid="support-reply-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}</Btn>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
